import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, Search, Calendar, CreditCard, Banknote, Building, CheckCircle2, MapPin, Star, BedDouble, Wifi, Wind, Tv, Bath, Coffee, Minus, Plus } from "lucide-react";
import api, { formatTRY, resolveImageUrl } from "@/lib/api";

/**
 * Two-column in-panel quick booking — a mini hotel detail page.
 *
 *   LEFT: scrollable list of room cards (photo, name, m², bed, capacity,
 *         amenity chips, original price line-through + discount % badge +
 *         new price, "Seç" button).
 *   RIGHT: sticky booking sidebar (dates, occupancy steppers, selected room
 *          summary, grand total, payment method, guest fields, submit).
 *
 * All payment methods are unlocked for staff regardless of public toggles.
 */
import { todayYMD as todayISO, plusDaysYMD as plusDaysISO } from "@/lib/dateUtils";

const AMENITY_ICONS = {
    "Klima": Wind, "Wi-Fi": Wifi, "wifi": Wifi, "TV": Tv, "LCD TV": Tv,
    "Banyo": Bath, "Mini Bar": Coffee, "Kahvaltı": Coffee,
};

export default function QuickBookingDrawer({ open, onClose, onCreated }) {
    const navigate = useNavigate();

    const [hotelQuery, setHotelQuery] = useState("");
    const [hotelSuggestions, setHotelSuggestions] = useState([]);
    const [hotel, setHotel] = useState(null);
    const [loadingHotel, setLoadingHotel] = useState(false);

    const [checkIn, setCheckIn] = useState(todayISO());
    const [checkOut, setCheckOut] = useState(plusDaysISO(2));
    const [adults, setAdults] = useState(2);
    const [children, setChildren] = useState(0);
    const [childAges, setChildAges] = useState([]);
    const [rooms, setRooms] = useState(1);

    const [selectedRoomId, setSelectedRoomId] = useState("");
    const [selectedPlanId, setSelectedPlanId] = useState("");
    const [roomQuotes, setRoomQuotes] = useState({}); // {roomId: quoteData}
    const [quotesLoading, setQuotesLoading] = useState(false);

    const [guest, setGuest] = useState({ first_name: "", last_name: "", email: "", phone: "" });
    const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
    const [specialRequests, setSpecialRequests] = useState("");

    const [banks, setBanks] = useState([]);
    const [posBanks, setPosBanks] = useState([]);
    const [selectedBankId, setSelectedBankId] = useState("");
    const [selectedPosBank, setSelectedPosBank] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (open) return;
        const t = setTimeout(() => {
            setHotelQuery(""); setHotelSuggestions([]); setHotel(null);
            setSelectedRoomId(""); setSelectedPlanId(""); setRoomQuotes({});
            setGuest({ first_name: "", last_name: "", email: "", phone: "" });
            setSpecialRequests(""); setSelectedBankId(""); setSelectedPosBank("");
            setError(""); setSuccess(null);
            setCheckIn(todayISO()); setCheckOut(plusDaysISO(2));
            setAdults(2); setChildren(0); setChildAges([]); setRooms(1);
        }, 350);
        return () => clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        api.get("/settings/public").then((r) => {
            const s = r.data || {};
            setBanks(s.bank_accounts || []);
            setPosBanks(s.pos_banks || []);
        }).catch(() => {});
    }, [open]);

    useEffect(() => {
        if (new Date(checkOut) <= new Date(checkIn)) {
            setCheckOut(plusDaysISO(1 + Math.round((new Date(checkIn) - Date.now()) / 86400000)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkIn]);

    useEffect(() => {
        setChildAges((prev) => {
            const next = [...prev];
            while (next.length < children) next.push(5);
            while (next.length > children) next.pop();
            return next;
        });
    }, [children]);

    useEffect(() => {
        if (!hotelQuery || hotelQuery.length < 2 || hotel) { setHotelSuggestions([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/hotels?q=${encodeURIComponent(hotelQuery)}&limit=8`);
                setHotelSuggestions(r.data.items || []);
            } catch { setHotelSuggestions([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [hotelQuery, hotel]);

    const pickHotel = async (h) => {
        setLoadingHotel(true);
        setHotelSuggestions([]);
        try {
            const r = await api.get(`/hotels/${h.id}`);
            setHotel(r.data);
            setHotelQuery(r.data.name);
            const rt = (r.data.room_types || [])[0];
            if (rt) {
                setSelectedRoomId(rt.id);
                const cheap = (rt.rate_plans || []).find((p) => p.is_cheapest) || (rt.rate_plans || [])[0];
                setSelectedPlanId(cheap?.id || "");
            }
        } catch (e) {
            setError("Otel detayı alınamadı");
        } finally {
            setLoadingHotel(false);
        }
    };

    const clearHotel = () => {
        setHotel(null); setHotelQuery(""); setSelectedRoomId(""); setSelectedPlanId(""); setRoomQuotes({});
    };

    // Batch quote fetcher — fetch one quote per room (cheapest plan), like the public hotel page does.
    const fetchAllQuotes = useCallback(async () => {
        if (!hotel) return;
        const rts = (hotel.room_types || []).filter((rt) => rt.capacity >= adults + children);
        if (!rts.length) { setRoomQuotes({}); return; }
        setQuotesLoading(true);
        try {
            const results = await Promise.all(
                rts.map(async (rt) => {
                    const plan = (rt.rate_plans || []).find((p) => p.is_cheapest) || (rt.rate_plans || [])[0];
                    if (!plan) return [rt.id, null];
                    try {
                        const r = await api.post("/bookings/quote", {
                            hotel_id: hotel.id,
                            room_type_id: rt.id,
                            rate_plan_id: plan.id,
                            check_in: checkIn, check_out: checkOut,
                            adults, children, child_ages: childAges, rooms,
                            insurance: false,
                        });
                        return [rt.id, { ...r.data, plan_id: plan.id, plan }];
                    } catch { return [rt.id, null]; }
                })
            );
            const map = {};
            for (const [rid, data] of results) if (data) map[rid] = data;
            setRoomQuotes(map);
        } finally { setQuotesLoading(false); }
    }, [hotel, checkIn, checkOut, adults, children, childAges, rooms]);

    useEffect(() => {
        if (!open || !hotel) return;
        const t = setTimeout(fetchAllQuotes, 300);
        return () => clearTimeout(t);
    }, [fetchAllQuotes, open, hotel]);

    const eligibleRooms = useMemo(() => (
        hotel ? (hotel.room_types || []).filter((rt) => rt.capacity >= adults + children) : []
    ), [hotel, adults, children]);

    const selectedRoom = (hotel?.room_types || []).find((r) => r.id === selectedRoomId);
    const selectedPlan = (selectedRoom?.rate_plans || []).find((p) => p.id === selectedPlanId);
    const selectedQuote = roomQuotes[selectedRoomId];
    const nights = selectedQuote?.nights || Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000));

    const submit = async () => {
        setError("");
        if (!hotel || !selectedRoomId || !selectedPlanId) { setError("Önce bir oda seçin"); return; }
        if (!selectedQuote || !selectedQuote.total || selectedQuote.total <= 0) { setError("Bu tarih aralığı için fiyat bulunamadı"); return; }
        if (!guest.first_name || !guest.last_name || !guest.email || !guest.phone) { setError("Misafir bilgilerini doldurun"); return; }
        if (paymentMethod === "bank_transfer" && banks.length > 0 && !selectedBankId) { setError("Banka hesabı seçin"); return; }
        if (paymentMethod === "card" && posBanks.length > 0 && !selectedPosBank) { setError("Sanal POS bankası seçin"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/bookings", {
                hotel_id: hotel.id, room_type_id: selectedRoomId, rate_plan_id: selectedPlanId,
                check_in: checkIn, check_out: checkOut,
                adults, children, child_ages: childAges, rooms,
                guest, special_requests: specialRequests || null, insurance: false,
                payment_method: paymentMethod,
                selected_bank_id: paymentMethod === "bank_transfer" ? selectedBankId : null,
                payment_bank: paymentMethod === "card" ? selectedPosBank : null,
                origin_url: window.location.origin,
            });
            setSuccess(r.data);
            onCreated && onCreated(r.data);
        } catch (e) {
            setError(e.response?.data?.detail || "Rezervasyon oluşturulamadı");
        } finally { setSubmitting(false); }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex" data-testid="quick-booking-drawer">
            <div className="flex-1 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
            <aside className="w-full max-w-[1180px] bg-slate-50 dark:bg-slate-950 h-full overflow-hidden shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">
                {/* Sticky header */}
                <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-5 py-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div>
                            <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Hızlı Rezervasyon</h2>
                            {hotel ? (
                                <p className="text-[11px] text-slate-500 truncate">
                                    {hotel.name} · {hotel.destination_name}
                                    <button onClick={clearHotel} className="ml-2 text-rose-500 font-bold hover:underline">Değiştir</button>
                                </p>
                            ) : (
                                <p className="text-[11px] text-slate-500">Panel içinden — yeni sekme açılmaz</p>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="qb-close-btn" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                </div>

                {success ? (
                    <SuccessView success={success} hotel={hotel} checkIn={checkIn} checkOut={checkOut} onAgain={() => setSuccess(null)} onClose={onClose} navigate={navigate} />
                ) : !hotel ? (
                    <HotelPicker hotelQuery={hotelQuery} setHotelQuery={setHotelQuery} hotelSuggestions={hotelSuggestions} loadingHotel={loadingHotel} pickHotel={pickHotel} />
                ) : (
                    /* Two-column layout */
                    <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_360px] overflow-hidden">
                        {/* LEFT — rooms list */}
                        <div className="overflow-y-auto p-4 lg:p-5 space-y-3">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="font-display text-base font-bold text-slate-900 dark:text-white">Oda Tipleri</h3>
                                <span className="text-[11px] text-slate-500">{eligibleRooms.length} oda · {nights} gece</span>
                            </div>
                            {quotesLoading && Object.keys(roomQuotes).length === 0 && (
                                <div className="text-xs text-slate-400 py-6 text-center">Fiyatlar yükleniyor...</div>
                            )}
                            {eligibleRooms.length === 0 ? (
                                <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg">Bu kapasite için uygun oda yok. Yetişkin/çocuk sayısını azaltın.</div>
                            ) : eligibleRooms.map((rt) => (
                                <RoomCard
                                    key={rt.id}
                                    room={rt}
                                    selected={rt.id === selectedRoomId}
                                    quote={roomQuotes[rt.id]}
                                    nights={nights}
                                    rooms={rooms}
                                    onSelect={() => {
                                        setSelectedRoomId(rt.id);
                                        const q = roomQuotes[rt.id];
                                        const pid = q?.plan_id || (rt.rate_plans?.find((p) => p.is_cheapest) || rt.rate_plans?.[0])?.id;
                                        if (pid) setSelectedPlanId(pid);
                                    }}
                                />
                            ))}
                        </div>

                        {/* RIGHT — sticky sidebar */}
                        <aside className="border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-y-auto">
                            <div className="p-4 lg:p-5 space-y-4">
                                {/* Dates */}
                                <div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Field label="Giriş">
                                            <div className="flex items-center gap-1.5 px-2 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                                <input data-testid="qb-checkin" type="date" value={checkIn} min={todayISO()} onChange={(e) => setCheckIn(e.target.value)} className="w-full bg-transparent outline-none text-xs font-semibold" />
                                            </div>
                                        </Field>
                                        <Field label="Çıkış">
                                            <div className="flex items-center gap-1.5 px-2 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                                <input data-testid="qb-checkout" type="date" value={checkOut} min={plusDaysISO(1)} onChange={(e) => setCheckOut(e.target.value)} className="w-full bg-transparent outline-none text-xs font-semibold" />
                                            </div>
                                        </Field>
                                    </div>
                                </div>

                                {/* Occupancy steppers */}
                                <div className="grid grid-cols-3 gap-2">
                                    <Stepper label="Yetişkin" value={adults} onChange={setAdults} min={1} max={6} testid="qb-adults" />
                                    <Stepper label="Çocuk" value={children} onChange={setChildren} min={0} max={4} testid="qb-children" />
                                    <Stepper label="Oda" value={rooms} onChange={setRooms} min={1} max={5} testid="qb-rooms" />
                                </div>
                                {children > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {childAges.map((age, i) => (
                                            <div key={i} className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1">
                                                <span className="text-[10px] text-slate-500">Çocuk {i + 1}</span>
                                                <select value={age} onChange={(e) => { const a = [...childAges]; a[i] = Number(e.target.value); setChildAges(a); }} className="bg-transparent text-xs font-bold outline-none">
                                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => <option key={n} value={n}>{n} yaş</option>)}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Selected room summary */}
                                {selectedRoom && selectedQuote ? (
                                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3 space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm font-bold text-slate-900 dark:text-white truncate">{selectedRoom.name}</div>
                                            <div className="text-[10px] text-emerald-600 dark:text-emerald-400">en ucuz</div>
                                        </div>
                                        {selectedPlan && <div className="text-[11px] text-slate-500">{selectedPlan.name} · {selectedPlan.board}</div>}
                                        <div className="flex justify-between text-xs text-slate-500"><span>{nights} gece × {rooms} oda</span><span>{formatTRY(selectedQuote.total)}</span></div>
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-3 text-center text-xs text-slate-400">Lütfen sol taraftan bir oda seçin</div>
                                )}

                                {/* Total */}
                                {selectedQuote && (
                                    <div className="flex items-baseline justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Toplam</div>
                                        <div className="text-2xl font-display font-bold text-sky-600 dark:text-sky-400">{formatTRY(selectedQuote.total)}</div>
                                    </div>
                                )}

                                {/* Guest fields */}
                                {selectedQuote && (
                                    <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Misafir Bilgileri</div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <input data-testid="qb-guest-first" placeholder="Ad" value={guest.first_name} onChange={(e) => setGuest((g) => ({ ...g, first_name: e.target.value }))} className="px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs outline-none focus:ring-2 focus:ring-sky-500" />
                                            <input data-testid="qb-guest-last" placeholder="Soyad" value={guest.last_name} onChange={(e) => setGuest((g) => ({ ...g, last_name: e.target.value }))} className="px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs outline-none focus:ring-2 focus:ring-sky-500" />
                                        </div>
                                        <input data-testid="qb-guest-email" placeholder="E-posta" type="email" value={guest.email} onChange={(e) => setGuest((g) => ({ ...g, email: e.target.value }))} className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs outline-none focus:ring-2 focus:ring-sky-500" />
                                        <input data-testid="qb-guest-phone" placeholder="Telefon" value={guest.phone} onChange={(e) => setGuest((g) => ({ ...g, phone: e.target.value }))} className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs outline-none focus:ring-2 focus:ring-sky-500" />
                                        <textarea data-testid="qb-special" placeholder="Özel not (opsiyonel)" value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={2} className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs outline-none focus:ring-2 focus:ring-sky-500 resize-none" />
                                    </div>
                                )}

                                {/* Payment */}
                                {selectedQuote && (
                                    <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Ödeme Yöntemi</div>
                                        <div className="grid grid-cols-3 gap-1.5">
                                            <PaymentChip active={paymentMethod === "bank_transfer"} onClick={() => setPaymentMethod("bank_transfer")} icon={<Banknote className="h-3.5 w-3.5" />} label="Havale" testid="qb-pm-bank" />
                                            <PaymentChip active={paymentMethod === "card"} onClick={() => setPaymentMethod("card")} icon={<CreditCard className="h-3.5 w-3.5" />} label="Kart" testid="qb-pm-card" />
                                            <PaymentChip active={paymentMethod === "at_hotel"} onClick={() => setPaymentMethod("at_hotel")} icon={<Building className="h-3.5 w-3.5" />} label="Otelde" testid="qb-pm-hotel" />
                                        </div>
                                        {paymentMethod === "bank_transfer" && banks.length > 0 && (
                                            <select data-testid="qb-bank" value={selectedBankId} onChange={(e) => setSelectedBankId(e.target.value)} className="w-full px-2 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold outline-none">
                                                <option value="">Banka Seçin</option>
                                                {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name}</option>)}
                                            </select>
                                        )}
                                        {paymentMethod === "card" && posBanks.length > 0 && (
                                            <select data-testid="qb-pos" value={selectedPosBank} onChange={(e) => setSelectedPosBank(e.target.value)} className="w-full px-2 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold outline-none">
                                                <option value="">Sanal POS</option>
                                                {posBanks.map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}
                                            </select>
                                        )}
                                    </div>
                                )}

                                {error && (
                                    <div data-testid="qb-error" className="text-xs font-semibold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 rounded-lg p-2">{error}</div>
                                )}

                                {selectedQuote && (
                                    <button onClick={submit} disabled={submitting} data-testid="qb-submit-btn" className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors">
                                        {submitting ? "Oluşturuluyor..." : "Rezervasyon Yap"}
                                    </button>
                                )}
                            </div>
                        </aside>
                    </div>
                )}
            </aside>
        </div>
    );
}

/* ============ Hotel picker (only when no hotel selected) ============ */
function HotelPicker({ hotelQuery, setHotelQuery, hotelSuggestions, loadingHotel, pickHotel }) {
    return (
        <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] flex-1">
            <div className="w-full max-w-md text-center mb-4">
                <BedDouble className="h-10 w-10 text-sky-500 mx-auto mb-2" />
                <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">Otel Seçin</h3>
                <p className="text-xs text-slate-500 mt-1">Otelin adının ilk birkaç harfini yazın</p>
            </div>
            <div className="relative w-full max-w-md">
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border-2 border-sky-200 dark:border-sky-900 focus-within:border-sky-500 shadow-sm">
                    <Search className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    <input data-testid="qb-hotel-search" value={hotelQuery} onChange={(e) => setHotelQuery(e.target.value)} placeholder="Örn: Moje More, May Thermal..." className="w-full bg-transparent outline-none text-sm" autoFocus />
                    {loadingHotel && <span className="text-[10px] text-slate-400">Yükleniyor...</span>}
                </div>
                {hotelSuggestions.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                        {hotelSuggestions.map((h) => (
                            <button key={h.id} type="button" onMouseDown={(e) => { e.preventDefault(); pickHotel(h); }} data-testid={`qb-hotel-pick-${h.id}`} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                                {h.main_image ? (
                                    <img src={resolveImageUrl(h.main_image)} alt="" className="h-12 w-16 object-cover rounded-lg flex-shrink-0" />
                                ) : (
                                    <div className="h-12 w-16 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0"><Building className="h-5 w-5 text-slate-400" /></div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-slate-900 dark:text-white truncate">{h.name}</div>
                                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5"><MapPin className="h-3 w-3" />{h.destination_name} · <Star className="h-3 w-3 text-amber-500" />{h.stars}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ============ Room Card ============ */
function RoomCard({ room, selected, quote, nights, rooms, onSelect }) {
    const photo = room.images?.[0] || null;
    const total = quote?.total || 0;
    const originalTotal = quote?.original_subtotal || total;
    const hasDiscount = total > 0 && originalTotal > total;
    const discountPct = hasDiscount ? Math.round((originalTotal - total) / originalTotal * 100) : 0;

    const amenities = (room.amenities || []).slice(0, 6);

    return (
        <div data-testid={`qb-room-${room.id}`} className={`flex gap-3 p-3 rounded-2xl border-2 transition-colors ${selected ? "border-sky-500 bg-sky-50/40 dark:bg-sky-950/20" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"}`}>
            {photo ? (
                <img src={resolveImageUrl(photo)} alt="" className="h-28 w-32 lg:h-32 lg:w-40 object-cover rounded-xl flex-shrink-0" />
            ) : (
                <div className="h-28 w-32 lg:h-32 lg:w-40 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0"><BedDouble className="h-8 w-8 text-slate-400" /></div>
            )}
            <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h4 className="font-display text-sm font-bold text-slate-900 dark:text-white truncate">{room.name}</h4>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            {room.size_sqm ? `${room.size_sqm} m² · ` : ""}{room.bed_type || ""} · {room.capacity} kişilik
                        </div>
                    </div>
                    <div className="text-right">
                        {quote ? (
                            <>
                                {hasDiscount && (
                                    <div className="flex items-center justify-end gap-1.5">
                                        <span className="text-[10px] line-through text-slate-400">{formatTRY(originalTotal)}</span>
                                        <span className="px-1.5 py-0.5 rounded-md bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-300 text-[10px] font-bold">-%{discountPct}</span>
                                    </div>
                                )}
                                <div className={`text-base font-display font-bold ${hasDiscount ? "text-fuchsia-600" : "text-slate-900 dark:text-white"}`}>{formatTRY(total)}</div>
                                <div className="text-[10px] text-slate-500">{nights} gece · {rooms} oda</div>
                            </>
                        ) : (
                            <div className="text-[11px] text-slate-400 italic">Fiyat yok</div>
                        )}
                    </div>
                </div>
                {amenities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {amenities.map((a, i) => {
                            const Icon = AMENITY_ICONS[a];
                            return (
                                <span key={i} className="inline-flex items-center gap-1 text-[10px] text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                    {Icon && <Icon className="h-2.5 w-2.5" />}{a}
                                </span>
                            );
                        })}
                    </div>
                )}
                <div className="mt-auto pt-2 flex justify-end">
                    <button onClick={onSelect} data-testid={`qb-room-select-${room.id}`} disabled={!quote || total <= 0} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-colors ${selected ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-sky-100 dark:hover:bg-sky-900"} disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed`}>
                        {selected ? "Seçildi" : "Seç"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ============ Helpers ============ */
function SuccessView({ success, hotel, checkIn, checkOut, onAgain, onClose, navigate }) {
    return (
        <div className="p-6 flex-1 flex flex-col items-center justify-center text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mb-4" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Rezervasyon Oluşturuldu</h3>
            <p className="text-sm text-slate-500 mb-4">Voucher No: <span className="font-mono font-bold text-slate-900 dark:text-white">{success.voucher_code || success.id.slice(0, 8).toUpperCase()}</span></p>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 w-full max-w-sm space-y-1.5 text-sm mb-4">
                <Row label="Misafir" value={`${success.guest?.first_name} ${success.guest?.last_name}`} />
                <Row label="Otel" value={hotel?.name} />
                <Row label="Tarih" value={`${checkIn} → ${checkOut}`} />
                <Row label="Toplam" value={formatTRY(success.total_amount)} bold />
            </div>
            <div className="flex gap-2 w-full max-w-sm">
                <button onClick={onAgain} data-testid="qb-new-booking-btn" className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-bold">Bir Daha</button>
                <button onClick={() => { navigate(`/admin/bookings?focus=${success.id}`); onClose && onClose(); }} data-testid="qb-go-bookings-btn" className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">Listeye Git</button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            {children}
        </div>
    );
}

function Stepper({ label, value, onChange, min, max, testid }) {
    const dec = () => onChange(Math.max(min, value - 1));
    const inc = () => onChange(Math.min(max, value + 1));
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-1.5 py-1.5">
                <button onClick={dec} disabled={value <= min} data-testid={`${testid}-dec`} className="h-6 w-6 rounded-md bg-white dark:bg-slate-700 disabled:opacity-30 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                <span data-testid={testid} className="text-sm font-bold">{value}</span>
                <button onClick={inc} disabled={value >= max} data-testid={`${testid}-inc`} className="h-6 w-6 rounded-md bg-white dark:bg-slate-700 disabled:opacity-30 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
            </div>
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex justify-between text-xs">
            <span className="text-slate-500">{label}</span>
            <span className={`text-slate-900 dark:text-white ${bold ? "font-bold" : ""}`}>{value}</span>
        </div>
    );
}

function PaymentChip({ active, onClick, icon, label, testid }) {
    return (
        <button onClick={onClick} data-testid={testid} className={`flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold transition-colors border ${active ? "bg-sky-500 text-white border-sky-500" : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"}`}>
            {icon} {label}
        </button>
    );
}
